/**
 * Card carousel: free continuous scroll + progress thumb.
 * Thumb/track dragging is rAF-coalesced for smooth 1:1 motion (no snap).
 */
(() => {
  if (customElements.get('card-carousel')) return;

  const DRAG_THRESHOLD = 8;
  const MOMENTUM_FRICTION = 0.95;
  const MOMENTUM_MIN_VELOCITY = 0.02;
  const THUMB_MOMENTUM_SCALE = 0.35;

  class CardCarousel extends HTMLElement {
    constructor() {
      super();

      this.track = null;
      this.progress = null;
      this.progressTrack = null;
      this.thumb = null;

      this.isPointerDown = false;
      this.isDraggingTrack = false;
      this.isDraggingThumb = false;
      this.hasDragged = false;
      this.ignoreClick = false;
      this.activePointerId = null;
      this.pointerDownLink = null;
      this.startX = 0;
      this.startY = 0;
      this.startScrollLeft = 0;
      this.lastX = 0;
      this.lastTime = 0;
      this.velocity = 0;
      this.momentumFrame = null;
      this.thumbStartX = 0;
      this.thumbStartLeft = 0;
      this.thumbLastX = 0;
      this.thumbLastTime = 0;
      this.thumbVelocity = 0;
      this.pendingThumbLeft = null;
      this.thumbRaf = null;

      this.onTrackPointerDown = this.onTrackPointerDown.bind(this);
      this.onTrackPointerMove = this.onTrackPointerMove.bind(this);
      this.onTrackPointerUp = this.onTrackPointerUp.bind(this);
      this.onThumbPointerDown = this.onThumbPointerDown.bind(this);
      this.onThumbPointerMove = this.onThumbPointerMove.bind(this);
      this.onThumbPointerUp = this.onThumbPointerUp.bind(this);
      this.onProgressTrackPointerDown = this.onProgressTrackPointerDown.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onCarouselInit = this.onCarouselInit.bind(this);
      this.flushThumbDrag = this.flushThumbDrag.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.progress = this.querySelector('[data-carousel-progress]');
      this.progressTrack = this.querySelector('[data-carousel-progress-track]');
      this.thumb = this.querySelector('[data-carousel-thumb]');

      if (!this.track) return;

      // Never inherit html { scroll-behavior: smooth }
      this.track.style.scrollBehavior = 'auto';

      this.track.addEventListener('pointerdown', this.onTrackPointerDown);
      this.track.addEventListener('click', this.onClickCapture, true);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('dragstart', (event) => event.preventDefault());

      if (this.thumb) {
        this.thumb.addEventListener('pointerdown', this.onThumbPointerDown);
      }

      if (this.progressTrack) {
        this.progressTrack.addEventListener('pointerdown', this.onProgressTrackPointerDown);
      }

      window.addEventListener('resize', this.onResize);
      this.addEventListener('theme:carousel:init', this.onCarouselInit);
      this.updateProgress();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      this.removeEventListener('theme:carousel:init', this.onCarouselInit);
      this.stopMomentum();
      this.cancelThumbRaf();
      this.removeDocumentListeners();
    }

    get maxScroll() {
      return Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
    }

    getThumbMetrics() {
      const trackWidth = this.progressTrack ? this.progressTrack.clientWidth : 0;
      const thumbWidth = this.thumb
        ? Math.max(
            this.thumb.offsetWidth || (this.track.clientWidth / Math.max(this.track.scrollWidth, 1)) * trackWidth,
            48
          )
        : 48;
      const maxThumbTravel = Math.max(trackWidth - thumbWidth, 0);
      return { trackWidth, thumbWidth, maxThumbTravel };
    }

    updateProgress() {
      if (!this.progress || !this.thumb || !this.progressTrack) return;

      const maxScroll = this.maxScroll;

      if (maxScroll <= 1) {
        this.progress.hidden = true;
        this.thumb.style.width = '100%';
        this.thumb.style.transform = 'translate3d(0, -50%, 0)';
        return;
      }

      this.progress.hidden = false;

      const { trackWidth, maxThumbTravel } = this.getThumbMetrics();
      const thumbWidth = Math.max((this.track.clientWidth / this.track.scrollWidth) * trackWidth, 48);
      const ratio = maxScroll === 0 ? 0 : this.track.scrollLeft / maxScroll;
      const thumbLeft = ratio * maxThumbTravel;

      this.thumb.style.width = `${thumbWidth}px`;
      this.thumb.style.transform = `translate3d(${thumbLeft}px, -50%, 0)`;
      this.thumb.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
    }

    setScrollLeft(left) {
      const target = Math.min(Math.max(left, 0), this.maxScroll);
      this.track.scrollLeft = target;
      return target;
    }

    scrollToLeft(left) {
      this.setScrollLeft(left);
      this.updateProgress();
    }

    scrollToRatio(ratio) {
      const clamped = Math.min(Math.max(ratio, 0), 1);
      this.scrollToLeft(clamped * this.maxScroll);
    }

    applyThumbLeft(nextLeft) {
      const { maxThumbTravel } = this.getThumbMetrics();
      const clampedLeft = Math.min(Math.max(nextLeft, 0), maxThumbTravel);
      const ratio = maxThumbTravel === 0 ? 0 : clampedLeft / maxThumbTravel;

      this.thumb.style.transform = `translate3d(${clampedLeft}px, -50%, 0)`;
      this.setScrollLeft(ratio * this.maxScroll);
      this.thumb.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
      return clampedLeft;
    }

    cancelThumbRaf() {
      if (this.thumbRaf) {
        cancelAnimationFrame(this.thumbRaf);
        this.thumbRaf = null;
      }
      this.pendingThumbLeft = null;
    }

    flushThumbDrag() {
      this.thumbRaf = null;
      if (this.pendingThumbLeft == null || !this.isDraggingThumb) return;
      this.applyThumbLeft(this.pendingThumbLeft);
      this.pendingThumbLeft = null;
    }

    scheduleThumbLeft(nextLeft) {
      this.pendingThumbLeft = nextLeft;
      if (this.thumbRaf != null) return;
      this.thumbRaf = requestAnimationFrame(this.flushThumbDrag);
    }

    stopMomentum() {
      if (this.momentumFrame) {
        cancelAnimationFrame(this.momentumFrame);
        this.momentumFrame = null;
      }
    }

    startMomentum(initialVelocity) {
      this.stopMomentum();
      this.velocity = initialVelocity;

      let last = performance.now();

      const step = (now) => {
        const dt = Math.min(now - last, 32);
        last = now;

        if (Math.abs(this.velocity) < MOMENTUM_MIN_VELOCITY) {
          this.velocity = 0;
          this.momentumFrame = null;
          this.updateProgress();
          return;
        }

        this.setScrollLeft(this.track.scrollLeft + this.velocity * dt);
        // Friction scaled to ~60fps baseline
        this.velocity *= Math.pow(MOMENTUM_FRICTION, dt / 16.67);

        if (this.track.scrollLeft <= 0 || this.track.scrollLeft >= this.maxScroll) {
          this.velocity = 0;
          this.setScrollLeft(this.track.scrollLeft);
          this.momentumFrame = null;
          this.updateProgress();
          return;
        }

        this.updateProgress();
        this.momentumFrame = requestAnimationFrame(step);
      };

      this.momentumFrame = requestAnimationFrame(step);
    }

    onScroll() {
      if (this.isDraggingThumb || this.isDraggingTrack) return;
      this.updateProgress();
    }

    onResize() {
      this.updateProgress();
    }

    onCarouselInit() {
      requestAnimationFrame(() => this.updateProgress());
    }

    isNavigableLink(link) {
      if (!link) return false;
      const href = link.getAttribute('href');
      if (!href || href === '#' || href.startsWith('javascript:')) return false;
      return true;
    }

    navigateToLink(link, event) {
      if (!this.isNavigableLink(link)) return;

      const href = link.href;
      if (event?.metaKey || event?.ctrlKey || event?.button === 1) {
        window.open(href, '_blank', 'noopener');
        return;
      }

      window.location.assign(href);
    }

    onTrackPointerDown(event) {
      if (event.button !== 0 && event.pointerType === 'mouse') return;
      if (event.target.closest('[data-carousel-progress]')) return;

      this.stopMomentum();
      this.cancelThumbRaf();
      this.isPointerDown = true;
      this.isDraggingTrack = false;
      this.hasDragged = false;
      this.velocity = 0;
      this.activePointerId = event.pointerId;
      this.pointerDownLink = event.target.closest('a[href]');
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.lastX = event.clientX;
      this.lastTime = performance.now();
      this.startScrollLeft = this.track.scrollLeft;

      document.addEventListener('pointermove', this.onTrackPointerMove, { passive: false });
      document.addEventListener('pointerup', this.onTrackPointerUp);
      document.addEventListener('pointercancel', this.onTrackPointerUp);
    }

    onTrackPointerMove(event) {
      if (!this.isPointerDown) return;
      if (this.activePointerId != null && event.pointerId !== this.activePointerId) return;

      const deltaX = event.clientX - this.startX;
      const deltaY = event.clientY - this.startY;
      const now = performance.now();
      const dt = Math.max(now - this.lastTime, 1);

      if (!this.isDraggingTrack && Math.abs(deltaX) > DRAG_THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY)) {
        this.isDraggingTrack = true;
        this.hasDragged = true;
        this.track.classList.add('is-dragging');
        this.track.setPointerCapture?.(event.pointerId);
      }

      if (!this.isDraggingTrack) return;

      event.preventDefault();
      // px/ms — used with dt in momentum
      this.velocity = (this.lastX - event.clientX) / dt;
      this.lastX = event.clientX;
      this.lastTime = now;
      this.setScrollLeft(this.startScrollLeft - deltaX);
      this.updateProgress();
    }

    onTrackPointerUp(event) {
      if (!this.isPointerDown) return;
      if (this.activePointerId != null && event.pointerId !== this.activePointerId) return;

      const link = this.pointerDownLink;
      const shouldNavigate = !this.hasDragged && this.isNavigableLink(link);
      const wasDragging = this.hasDragged;

      this.isPointerDown = false;
      this.isDraggingTrack = false;
      this.activePointerId = null;
      this.pointerDownLink = null;
      this.track.classList.remove('is-dragging');
      this.removeDocumentListeners();

      if (wasDragging) {
        this.ignoreClick = true;
        window.setTimeout(() => {
          this.ignoreClick = false;
          this.hasDragged = false;
        }, 50);

        if (Math.abs(this.velocity) > MOMENTUM_MIN_VELOCITY) {
          this.startMomentum(this.velocity);
        } else {
          this.updateProgress();
        }
        return;
      }

      if (shouldNavigate) {
        this.ignoreClick = true;
        window.setTimeout(() => {
          this.ignoreClick = false;
        }, 50);
        this.navigateToLink(link, event);
      }
    }

    onClickCapture(event) {
      if (!this.ignoreClick && !this.hasDragged) return;
      event.preventDefault();
      event.stopPropagation();
      this.hasDragged = false;
      this.ignoreClick = false;
    }

    onThumbPointerDown(event) {
      event.preventDefault();
      event.stopPropagation();

      this.stopMomentum();
      this.cancelThumbRaf();
      this.isDraggingThumb = true;
      this.thumbStartX = event.clientX;
      this.thumbStartLeft = this.getThumbLeft();
      this.thumbLastX = event.clientX;
      this.thumbLastTime = performance.now();
      this.thumbVelocity = 0;
      this.thumb.classList.add('is-dragging');
      this.track.classList.add('is-dragging');
      this.thumb.setPointerCapture?.(event.pointerId);

      document.addEventListener('pointermove', this.onThumbPointerMove, { passive: false });
      document.addEventListener('pointerup', this.onThumbPointerUp);
      document.addEventListener('pointercancel', this.onThumbPointerUp);
    }

    onThumbPointerMove(event) {
      if (!this.isDraggingThumb) return;
      event.preventDefault();

      const now = performance.now();
      const dt = Math.max(now - this.thumbLastTime, 1);
      const dx = event.clientX - this.thumbLastX;
      // Convert thumb px/ms into approximate track scroll velocity
      const { maxThumbTravel } = this.getThumbMetrics();
      const scrollPerThumbPx = maxThumbTravel > 0 ? this.maxScroll / maxThumbTravel : 0;
      this.thumbVelocity = (dx / dt) * scrollPerThumbPx;
      this.thumbLastX = event.clientX;
      this.thumbLastTime = now;

      const nextLeft = this.thumbStartLeft + (event.clientX - this.thumbStartX);
      this.scheduleThumbLeft(nextLeft);
    }

    onThumbPointerUp() {
      if (!this.isDraggingThumb) return;

      // Apply any pending frame before release
      if (this.pendingThumbLeft != null) {
        this.applyThumbLeft(this.pendingThumbLeft);
      }
      this.cancelThumbRaf();

      this.isDraggingThumb = false;
      this.thumb.classList.remove('is-dragging');
      this.track.classList.remove('is-dragging');
      this.removeDocumentListeners();

      if (Math.abs(this.thumbVelocity) > MOMENTUM_MIN_VELOCITY) {
        this.startMomentum(this.thumbVelocity * THUMB_MOMENTUM_SCALE);
      } else {
        this.updateProgress();
      }
    }

    onProgressTrackPointerDown(event) {
      if (event.target === this.thumb || this.thumb.contains(event.target)) return;
      if (event.button !== 0 && event.pointerType === 'mouse') return;

      event.preventDefault();
      this.stopMomentum();
      this.cancelThumbRaf();

      const rect = this.progressTrack.getBoundingClientRect();
      const { thumbWidth, maxThumbTravel } = this.getThumbMetrics();
      const clickX = event.clientX - rect.left - thumbWidth / 2;
      const nextLeft = Math.min(Math.max(clickX, 0), maxThumbTravel);

      // Jump thumb under cursor, then continue as a drag (feels continuous)
      this.applyThumbLeft(nextLeft);
      this.onThumbPointerDown(event);
      // Re-baseline so subsequent move is relative to the new position
      this.thumbStartLeft = nextLeft;
      this.thumbStartX = event.clientX;
    }

    getThumbLeft() {
      const transform = window.getComputedStyle(this.thumb).transform;
      if (!transform || transform === 'none') return 0;
      const match = transform.match(/matrix\((.+)\)/);
      if (!match) return 0;
      const values = match[1].split(',').map((value) => parseFloat(value.trim()));
      return values[4] || 0;
    }

    removeDocumentListeners() {
      document.removeEventListener('pointermove', this.onTrackPointerMove);
      document.removeEventListener('pointerup', this.onTrackPointerUp);
      document.removeEventListener('pointercancel', this.onTrackPointerUp);
      document.removeEventListener('pointermove', this.onThumbPointerMove);
      document.removeEventListener('pointerup', this.onThumbPointerUp);
      document.removeEventListener('pointercancel', this.onThumbPointerUp);
    }
  }

  customElements.define('card-carousel', CardCarousel);
})();
